import React, { useEffect, useRef } from 'react'
import Lenis from '@studio-freight/lenis'

interface LenisScrollOptions {
  smoothWheel?: boolean
  smoothTouch?: boolean
  duration?: number
  easing?: (t: number) => number
  direction?: 'vertical' | 'horizontal'
  gestureDirection?: 'vertical' | 'horizontal'
  smooth?: boolean
  mouseMultiplier?: number
  touchMultiplier?: number
}

const useLenisScroll = (options: LenisScrollOptions = {}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const lenisRef = useRef<Lenis | null>(null)

  useEffect(() => {
    if (!containerRef.current) return
    
    // Check if device is mobile/touch device
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    const isSmallScreen = window.innerWidth < 768 // md breakpoint
    
    // Don't initialize Lenis on mobile devices - use native scrolling instead
    if (isTouchDevice || isSmallScreen) {
      return
    }

    const defaultOptions: LenisScrollOptions = {
      smoothWheel: true,
      smoothTouch: false, // Disable on touch to avoid conflicts
      duration: 1.2,
      easing: (t: number): number => Math.min(1, 1.001 - Math.pow(2, -5 * t)),
      direction: 'vertical' as const,
      gestureDirection: 'vertical' as const,
      smooth: true,
      mouseMultiplier: 2,
      touchMultiplier: 0, // Disable touch multiplier
      ...options 
    }

    lenisRef.current = new Lenis({
      wrapper: containerRef.current,
      content: containerRef.current,
      ...defaultOptions
    })

    const raf = (time: number): void => {
      lenisRef.current?.raf(time)
      requestAnimationFrame(raf)
    }
    requestAnimationFrame(raf)

    return () => {
      lenisRef.current?.destroy()
    }
  }, [])

  return containerRef
}

export default useLenisScroll